import { LogManager } from '@src/utility/Logger';

import {
  captionVariantLanguageForConfig,
  normalizeCaptionVariantLanguage,
} from './karaokeLanguageVariants';

import type { KaraokeTemplateMetadata } from './types';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeStore } from '@src/stores-v2/KaraokeStore';
import type { Showcase } from '@src/types.ts/story';
import type { AspectRatio } from '@src/types.ts/video';

const logger = LogManager.getLogger('service:KaraokeTemplateVariantService');

export interface VariantMetadata {
  sourceConfig: KaraokeConfig;
  sourceAspectRatio: AspectRatio;
  targetAspectRatio: AspectRatio;
  modificationType: 'scaled' | 'other';
}

/**
 * Minimal subset of AlbumRepository needed by KaraokeTemplateVariantService
 */
export interface AlbumRepositoryLike {
  update(organization: Partial<Showcase> & { id: string }): Promise<void>;
}

/**
 * Minimal subset of TextBrandingService needed by KaraokeTemplateVariantService
 */
export interface TextBrandingServiceLike {
  /**
   * Get current organization reference
   */
  getOrganization(): Showcase;

  /**
   * Create a new karaoke template in DatoCMS
   */
  createKaraokeTemplateInDato(
    config: KaraokeConfig,
    metadata: KaraokeTemplateMetadata,
  ): Promise<string>;

  /**
   * Update an existing karaoke template in DatoCMS
   */
  updateKaraokeTemplateInDato(
    templateId: string,
    config: KaraokeConfig,
    metadata: KaraokeTemplateMetadata,
  ): Promise<void>;

  /**
   * Update organization's in-memory branding array with template
   * Returns the updated organization object (does not persist)
   */
  formatOrganizationBrandingFields(
    config: KaraokeConfig | Record<string, any>,
    title: string,
    type: 'basic' | 'karaoke',
    id?: string,
    presetId?: string,
  ): Showcase;

  /**
   * PLAT-11416 — whether the environment supports the caption-template
   * `language` field (waits for the schema probe). Gates the language
   * dimension of variant lookup/creation so it can never diverge from what
   * the Dato write layer actually persists.
   */
  ensureCaptionTemplateLanguagesLoaded(): Promise<boolean>;
}

export interface KaraokeTemplateVariantServiceDeps {
  brandingService: TextBrandingServiceLike;
  albumRepository: AlbumRepositoryLike;
}

/**
 * Minimal interface for karaoke template variant operations
 * Used by editor commands to avoid tight coupling
 */
export interface IKaraokeTemplateVariantService {
  /**
   * Track a config modification that needs a template variant created
   */
  trackModification(
    configId: string,
    modifiedConfig: KaraokeConfig,
    metadata: VariantMetadata,
  ): void;

  /**
   * Create or update template variants for all tracked configs
   */
  ensureVariantsForConfigs(
    currentAspectRatio: AspectRatio,
  ): Promise<Map<string, string>>;

  /**
   * Apply runtime karaoke config updates after template variants are persisted
   */
  applyTemplateVariantUpdates(
    editor: IVideoEditor,
    karaokeStore: KaraokeStore,
    configIdToTemplateIdMap: Map<string, string>,
  ): void;

  /**
   * Clear all tracked modifications without persisting
   */
  clearModifications(): void;
}

/**
 * Service responsible for creating and updating karaoke template variants
 * during aspect ratio changes.
 *
 * Separates template variant lifecycle management from discovery/lookup logic.
 */
export class KaraokeTemplateVariantService {
  private trackedConfigs: Map<
    string,
    { config: KaraokeConfig; metadata: VariantMetadata }
  > = new Map();

  private readonly brandingService: TextBrandingServiceLike;
  private readonly albumRepository: AlbumRepositoryLike;

  constructor(deps: KaraokeTemplateVariantServiceDeps) {
    this.brandingService = deps.brandingService;
    this.albumRepository = deps.albumRepository;
  }

  /**
   * Track a config that needs a template variant created
   */
  trackModification(
    configId: string,
    modifiedConfig: KaraokeConfig,
    metadata: VariantMetadata,
  ): void {
    this.trackedConfigs.set(configId, { config: modifiedConfig, metadata });
  }

  /**
   * Create or update template variants for all tracked configs
   * Returns a mapping of configId -> newTemplateId
   *
   * Note: formatOrganizationBrandingFields modifies videoCreator.organization internally
   */
  async ensureVariantsForConfigs(
    currentAspectRatio: AspectRatio,
  ): Promise<Map<string, string>> {
    const results = new Map<string, string>();

    if (this.trackedConfigs.size === 0) {
      return results;
    }

    logger.info(
      `Processing ${this.trackedConfigs.size} karaoke template variant(s)`,
    );

    // PLAT-11416 — language tagging only where the schema supports it;
    // otherwise the lookup would skip records the create can't distinguish,
    // minting duplicate neutral variants on every AR switch.
    const languagesSupported =
      await this.brandingService.ensureCaptionTemplateLanguagesLoaded();

    for (const [configId, { config, metadata }] of this.trackedConfigs) {
      try {
        const sourceConfig = metadata.sourceConfig;
        const originalTemplateId = sourceConfig.parent_template_id;

        if (!originalTemplateId) {
          logger.warn(
            `Config ${configId} has no parent_template_id, skipping variant creation`,
          );
          continue;
        }

        // Get current organization state (formatOrganizationBrandingFields will mutate this)
        const organization = this.brandingService.getOrganization();

        // Find original template to get title and presetId
        const originalTemplate = organization.karaokeTextBranding?.find(
          t => t.id === originalTemplateId,
        );

        if (!originalTemplate) {
          logger.warn(
            `Original template ${originalTemplateId} not found for config ${configId}`,
          );
          continue;
        }

        const title =
          originalTemplate.title ||
          `Untitled Variant (${metadata.targetAspectRatio})`;

        let presetId: string;
        // Determine presetId - if original doesn't have one, use its own ID (retroactive assignment)
        // If this is the first variant being created and original doesn't have presetId,
        // retroactively assign presetId to the original template
        if (!originalTemplate.presetId) {
          presetId = originalTemplateId;

          logger.info(
            `Retroactively assigning presetId=${presetId} to original template ${originalTemplateId}`,
          );

          await this.brandingService.updateKaraokeTemplateInDato(
            originalTemplateId,
            sourceConfig,
            {
              title,
              aspectRatio: metadata.sourceAspectRatio,
              presetId,
            },
          );

          // Update in-memory presetId (will propagate to organization)
          originalTemplate.presetId = presetId;
        } else {
          presetId = originalTemplate.presetId;
        }

        // PLAT-11416: a variant belongs to (aspect ratio × language) — a
        // sentinel-language config keeps today's language-neutral records,
        // a translated config looks up and creates records tagged with its
        // language, so an AR switch under Japanese can neither reuse the
        // Latin variant nor mint a neutral record carrying Japanese styling.
        const variantLanguage = languagesSupported
          ? captionVariantLanguageForConfig(config.language)
          : null;

        // Check if variant already exists for this aspect ratio + language
        const existingVariant = organization.karaokeTextBranding?.find(
          t =>
            t.presetId === presetId &&
            t.aspectRatio === currentAspectRatio &&
            normalizeCaptionVariantLanguage(t.language) === variantLanguage &&
            t.id !== originalTemplateId,
        );

        let newTemplateId: string;

        if (existingVariant?.id) {
          // Use existing variant as-is (templates can diverge across aspect ratios)
          logger.info(
            `Using existing variant ${existingVariant.id} for presetId=${presetId}, AR=${currentAspectRatio}, lang=${variantLanguage ?? 'neutral'}`,
          );
          newTemplateId = existingVariant.id;
        } else {
          // Create new variant
          logger.info(
            `Creating new variant for presetId=${presetId}, AR=${currentAspectRatio}, lang=${variantLanguage ?? 'neutral'}`,
          );
          newTemplateId =
            await this.brandingService.createKaraokeTemplateInDato(config, {
              title,
              aspectRatio: currentAspectRatio,
              presetId,
              language: variantLanguage ?? undefined,
            });
          // Update organization's in-memory array (formatOrganizationBrandingFields mutates internally)
          this.brandingService.formatOrganizationBrandingFields(
            config,
            title,
            'karaoke',
            newTemplateId,
            presetId,
          );
          // PLAT-11416 — the in-memory record is built from the config
          // alone; stitch the family/language metadata so lookups later in
          // this session see it (re-fetch: format may replace the org object)
          const createdRecord = this.brandingService
            .getOrganization()
            .karaokeTextBranding?.find(t => t.id === newTemplateId);
          if (createdRecord) {
            createdRecord.presetId = createdRecord.presetId || presetId;
            if (variantLanguage) createdRecord.language = variantLanguage;
          }
        }

        results.set(configId, newTemplateId);
      } catch (error) {
        logger.error(
          `Failed to create/update variant for config ${configId}:`,
          error,
        );
      }
    }

    this.trackedConfigs.clear();

    // Single persistence call for all template updates
    if (results.size > 0) {
      // Get final organization state (formatOrganizationBrandingFields has mutated videoCreator.organization)
      const finalOrganization = this.brandingService.getOrganization();
      await this.albumRepository.update(finalOrganization);
      logger.info(
        `Persisted ${results.size} template variant(s) to organization`,
      );
    }

    return results;
  }

  /**
   * Apply runtime karaoke config updates after template variants are persisted.
   * Updates parent_template_id references and syncs karaoke store.
   */
  applyTemplateVariantUpdates(
    editor: IVideoEditor,
    karaokeStore: KaraokeStore,
    configIdToTemplateIdMap: Map<string, string>,
  ): void {
    if (configIdToTemplateIdMap.size === 0) {
      return;
    }

    const configsMap = editor.getKaraokeConfigs();

    for (const [configId, newTemplateId] of configIdToTemplateIdMap) {
      const config = configsMap.get(configId);
      if (config) {
        editor.updateKaraokeConfig({
          ...config,
          parent_template_id: newTemplateId,
        });
      }
    }

    karaokeStore.syncFromEditor(editor);
  }

  /**
   * Clear all tracked modifications without persisting
   */
  clearModifications(): void {
    this.trackedConfigs.clear();
  }
}
